import React, { useState, useEffect } from "react";
import { ExpandLess, ExpandMore, OpenInNew } from "@mui/icons-material";

import { ModelBase, FeatureSource } from "@models";

import API from "@utilities/smart_api";
import FlexBox from "@components/layout/FlexBox";
import LoadingBox from "@components/input/LoadingBox";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";

import { BasicRule } from "./basic_rules";

const api = API.getInstance();

export interface RefEntry {
  key: string;
  section: string;
  name: string;
  subtitle: string;
  keywords: string[];
  rule?: BasicRule;
  obj?: ModelBase;
  see_also?: string[]; // Names of related Rules Definitions.
}

interface Props {
  entry: RefEntry;
  auto_expand?: boolean;
}

export default function RulesReferenceEntry({ entry, auto_expand = false }: Props) {
  // null until the user toggles it, so it follows auto_expand until then.
  const [toggled, setToggled] = useState<boolean | null>(null);
  const [load_failed, setLoadFailed] = useState(false);
  const [, setLoadedCount] = useState(0);

  const expanded = toggled ?? auto_expand;
  const obj = entry.obj;
  const needs_load = expanded && !!obj && !obj.loaded && obj._id !== "" && !load_failed;

  useEffect(() => {
    if (!needs_load || !obj) {
      return;
    }
    let active = true;
    // loadObject fills in obj in place, so just re-render once it's done.
    api.loadObject(obj).then(() => {
      if (active) {
        setLoadedCount(c => c + 1);
      }
    }).catch(ex => {
      console.error(ex);
      if (active) {
        setLoadFailed(true);
      }
    });
    return () => { active = false; };
  }, [needs_load, obj]);

  const renderConstantExtras = (obj: ModelBase) => {
    const c = obj as any;
    const lines: string[] = [];
    if (obj.data_type === "skill" && c.use_ability_score && c.use_ability_score !== "None") {
      lines.push(`Ability: ${c.use_ability_score}`);
    } else if (obj.data_type === "armor_type") {
      if (c.dex_bonus_max === null) {
        lines.push("Add full Dexterity modifier to AC.");
      } else if (c.dex_bonus_max === true) {
        lines.push("Add Dexterity modifier to AC (max 2).");
      } else if (c.dex_bonus_max === false) {
        lines.push("Doesn't add Dexterity modifier to AC.");
      }
      if (c.stealth_disadvantage) {
        lines.push("Disadvantage on Dexterity (Stealth) checks.");
      }
    } else if (obj.data_type === "weapon_keyword") {
      if (c.use_ability_score && c.use_ability_score !== "Default") {
        lines.push(`Uses ${c.use_ability_score} for attack and damage.`);
      }
      if (c.alternate_ability_score && c.alternate_ability_score !== "None") {
        lines.push(`Can use ${c.alternate_ability_score} instead of STR.`);
      }
    } else if (obj.data_type === "language" && c.script) {
      lines.push(`Script: ${c.script}`);
    }
    return lines.map(l => (
      <div key={l} className="small_label">{ l }</div>
    ));
  };

  const renderFeatureBases = (fs: FeatureSource) => {
    const show_level = fs.data_type === "game_class" || fs.data_type === "subclass";
    const fbs = [...fs.feature_bases].sort((a, b) =>
      a.level === b.level ? a.position_at_level - b.position_at_level : a.level - b.level
    );
    return fbs.map(fb => {
      const levels = [fb.level, ...fb.repeat_levels].filter(l => l > 0);
      return (
        <FlexBox key={fb.true_id || fb.name} direction="column" style={{ marginTop: "8px" }}>
          <div style={{ fontWeight: "bold" }}>
            { fb.name }
            { show_level && levels.length > 0 &&
              <span className="small_label gray"> (Level{ levels.length > 1 ? "s" : "" } { levels.join(", ") })</span>
            }
          </div>
          { fb.description &&
            <DisplayStringWithReplacements display_string={fb.description} cfs={fs} />
          }
        </FlexBox>
      );
    });
  };

  const renderDetails = () => {
    if (entry.rule) {
      return (
        <div>
          { splitByEdition(entry.rule.description).map((part, i) => (
            <p key={i} style={{ margin: "0 0 8px 0" }}>
              { part.edition && <strong>{ part.edition }: </strong> }
              { part.text }
            </p>
          ))}
        </div>
      );
    }
    if (!obj) {
      return null;
    }
    const page_url = getPageURL(obj);
    const page_link = page_url &&
      <a href={page_url} target="_blank" rel="noopener noreferrer"
        className="small_label"
        style={{ display: "inline-flex", alignItems: "center", gap: "4px", marginBottom: "4px" }}>
        Open { entry.name } page <OpenInNew fontSize="inherit" />
      </a>;
    if (needs_load) {
      return (
        <FlexBox direction="column">
          { page_link }
          <LoadingBox />
        </FlexBox>
      );
    }
    return (
      <FlexBox direction="column">
        { page_link }
        { renderConstantExtras(obj) }
        { obj.description &&
          <DisplayStringWithReplacements display_string={obj.description}
            cfs={obj instanceof FeatureSource ? obj : undefined} />
        }
        { obj instanceof FeatureSource && renderFeatureBases(obj) }
        { entry.see_also && entry.see_also.length > 0 &&
          <div className="small_label" style={{ marginTop: "4px" }}>
            <i>See also</i> { entry.see_also.join(", ") }.
          </div>
        }
      </FlexBox>
    );
  };

  return (
    <FlexBox direction="column" style={{ borderBottom: "1px solid rgba(128,128,128,0.3)" }}>
      <FlexBox className="clickable" direction="row" style={{ padding: "4px 8px", alignItems: "center" }}
        onClick={() => setToggled(!expanded)}>
        <FlexBox direction="column" style={{ flex: 1 }}>
          <span>{ entry.name }</span>
          { entry.subtitle &&
            <span className="small_label gray">{ entry.subtitle }</span>
          }
        </FlexBox>
        { expanded ? <ExpandLess /> : <ExpandMore /> }
      </FlexBox>
      { expanded &&
        <div style={{ padding: "4px 12px 12px 12px" }}>
          { renderDetails() }
        </div>
      }
    </FlexBox>
  );
}

// Splits a rule into paragraphs at each sentence starting with "2014:" or "2024:".
// Inline notes like "(2014: ...)" are left where they are.
function splitByEdition(description: string): { edition: string, text: string }[] {
  return description.split(/(?<=\.\s)(?=20(?:14|24): )/).map(part => {
    const match = part.match(/^(20(?:14|24)): (.*)$/s);
    return match ? { edition: match[1], text: match[2].trim() } : { edition: "", text: part.trim() };
  });
}

// The object's own page, if it has one. The built-in constants (skills, senses,
// armor types, etc.) are plain objects rather than ModelBase instances and have no page.
function getPageURL(obj: ModelBase): string {
  if (!(obj instanceof ModelBase) || obj._id === "") {
    return "";
  }
  return obj.linkURL || `/${obj.data_type}/${obj._id}`;
}
